/**
 * Parser de arquivos SPED que detecta automaticamente o tipo:
 * - SPED ICMS/IPI (EFD ICMS/IPI)
 * - SPED PIS/COFINS (EFD Contribuições)
 * 
 * A diferença está na estrutura do registro 0000:
 * 
 * SPED ICMS/IPI:
 * |0000|COD_VER|COD_FIN|DT_INI|DT_FIN|NOME|CNPJ|CPF|UF|IE|COD_MUN|...
 * fields: [_, 0000, COD_VER, COD_FIN, DT_INI, DT_FIN, NOME, CNPJ, ...]
 * 
 * SPED PIS/COFINS:
 * |0000|COD_VER|TIPO_ESCRIT|IND_SIT_ESP|NUM_REC_ANTERIOR|DT_INI|DT_FIN|NOME|CNPJ|UF|COD_MUN|...
 * fields: [_, 0000, COD_VER, TIPO_ESCRIT, IND_SIT_ESP, NUM_REC_ANTERIOR, DT_INI, DT_FIN, NOME, CNPJ, ...]
 */

/**
 * Formata uma data do formato DDMMYYYY para DD/MM/YYYY
 */
const formatDate = (raw) => {
  if (!raw || raw.length !== 8) return raw;
  return `${raw.substring(0, 2)}/${raw.substring(2, 4)}/${raw.substring(4)}`;
};

/**
 * Detecta o tipo de SPED baseado na estrutura do registro 0000
 * 
 * Lógica de detecção:
 * - No SPED ICMS/IPI, fields[3] é COD_FIN (valores: 0, 1, 2, 3, 4) 
 * - No SPED PIS/COFINS, fields[3] é TIPO_ESCRIT (valores: 0, 1)
 * - Para diferenciar, verificamos se fields[4] parece uma data (8 dígitos numéricos)
 *   - Se sim → ICMS/IPI (DT_INI está em fields[4])
 *   - Se não → PIS/COFINS (DT_INI está em fields[6])
 */
const detectSpedType = (fields) => {
  // fields[4] no ICMS é DT_INI (formato DDMMYYYY - 8 dígitos)
  // fields[4] no PIS é IND_SIT_ESP (geralmente vazio ou 1-2 dígitos)
  const field4 = fields[4] || '';
  const field6 = fields[6] || '';
  
  // Se field4 tem exatamente 8 dígitos numéricos, é uma data → ICMS/IPI
  const isField4Date = /^\d{8}$/.test(field4);
  
  // Se field6 tem exatamente 8 dígitos numéricos, é uma data → PIS/COFINS
  const isField6Date = /^\d{8}$/.test(field6);
  
  if (isField4Date) {
    return 'ICMS_IPI';
  } else if (isField6Date) {
    return 'PIS_COFINS';
  }
  
  // Fallback: tentar detectar pelo número de campos preenchidos
  // SPED PIS/COFINS tem mais campos antes da data
  const numFieldsBeforeDate = isField6Date ? 6 : 4;
  return numFieldsBeforeDate > 5 ? 'PIS_COFINS' : 'ICMS_IPI';
};

/**
 * Extrai metadados do registro 0000 baseado no tipo detectado
 */
const extractReg0000Metadata = (fields, spedType) => {
  if (spedType === 'PIS_COFINS') {
    // SPED PIS/COFINS structure:
    // |0000|COD_VER|TIPO_ESCRIT|IND_SIT_ESP|NUM_REC_ANTERIOR|DT_INI|DT_FIN|NOME|CNPJ|UF|COD_MUN|...
    return {
      periodoInicio: formatDate(fields[6]),
      periodoFim: formatDate(fields[7]),
      razaoSocial: fields[8] || '',
      cnpj: fields[9] || '',
      codVer: fields[2] || '',
      tipoEscrit: fields[3] || '',
    };
  } else {
    // SPED ICMS/IPI structure:
    // |0000|COD_VER|COD_FIN|DT_INI|DT_FIN|NOME|CNPJ|CPF|UF|IE|COD_MUN|...
    return {
      periodoInicio: formatDate(fields[4]),
      periodoFim: formatDate(fields[5]),
      razaoSocial: fields[6] || '',
      cnpj: fields[7] || '',
      codVer: fields[2] || '',
      codFin: fields[3] || '',
    };
  }
};

export const parseSpedFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const lines = content.split('\n');
        
        const stats = {
          fileName: file.name,
          fileSize: file.size,
          cnpj: '',
          razaoSocial: '',
          periodoInicio: '',
          periodoFim: '',
          tipoSped: 'Desconhecido',
          totalNfe: 0,
          totalCte: 0,
          totalNfce: 0,
          totalParticipantes: 0,
          totalItens: 0,
          isValid: false,
          errors: []
        };

        let isSped = false;
        let detectedType = null;

        // Processar primeiras linhas para metadados e detectar tipo
        for (let i = 0; i < Math.min(lines.length, 100); i++) {
          const line = lines[i].trim();
          if (!line.startsWith('|')) continue;
          
          const fields = line.split('|');
          
          // Registro 0000 - Abertura do arquivo digital
          if (fields[1] === '0000') {
            isSped = true;
            
            // Detectar tipo de SPED pela estrutura do 0000
            detectedType = detectSpedType(fields);
            
            // Extrair metadados baseado no tipo detectado
            const metadata = extractReg0000Metadata(fields, detectedType);
            
            stats.periodoInicio = metadata.periodoInicio;
            stats.periodoFim = metadata.periodoFim;
            stats.razaoSocial = metadata.razaoSocial;
            stats.cnpj = metadata.cnpj;
            stats.tipoSped = detectedType;
            
            break; // Encontrou o 0000, pode parar essa fase
          }
        }

        if (!isSped) {
          stats.errors.push("Arquivo não parece ser um SPED válido (Registro 0000 não encontrado)");
          resolve(stats);
          return;
        }

        stats.isValid = true;

        let hasBlockE = false; // Bloco E - Apuração ICMS/IPI
        let hasBlockM = false; // Bloco M - Apuração PIS/COFINS
        let hasBlockF = false; // Bloco F - Demais Documentos (exclusivo PIS/COFINS)

        // Processar contagens
        for (const line of lines) {
          if (!line.startsWith('|')) continue;
          
          // Detectar blocos principais
          if (line.startsWith('|E')) hasBlockE = true;
          if (line.startsWith('|M')) hasBlockM = true;
          if (line.startsWith('|F')) hasBlockF = true;
          
          // Otimização: Pegar apenas o registro
          const secondPipeIndex = line.indexOf('|', 1);
          if (secondPipeIndex === -1) continue;
          
          const reg = line.substring(1, secondPipeIndex);
          
          if (reg === 'C100') {
            // Verificar modelo para distinguir NFCe (65) de NFe (55)
            const parts = line.split('|');
            const modelo = parts[5];
            if (modelo === '55') stats.totalNfe++;
            else if (modelo === '65') stats.totalNfce++;
            else stats.totalNfe++; // Fallback
          } else if (reg === 'D100') {
            stats.totalCte++;
          } else if (reg === '0150') {
            stats.totalParticipantes++;
          } else if (reg === 'C170' || reg === 'C190') {
            stats.totalItens++;
          }
        }
        
        // Confirmar/refinar tipo com base nos blocos encontrados
        // Se já detectamos via 0000, apenas confirmamos
        // Se não conseguimos detectar pelo 0000, usamos os blocos
        if (detectedType) {
          stats.tipoSped = detectedType;
        } else if (hasBlockM || hasBlockF) {
          // Bloco M (Apuração Contribuição) e F (Demais docs) são exclusivos do PIS/COFINS
          stats.tipoSped = 'PIS_COFINS';
        } else if (hasBlockE) {
          stats.tipoSped = 'ICMS_IPI';
        } else {
          stats.tipoSped = 'ICMS_IPI'; // Default
        }

        resolve(stats);

      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
    reader.readAsText(file, 'ISO-8859-1'); // SPED geralmente é ISO-8859-1
  });
};

